import { useState } from "react"
import { Link } from "@inertiajs/react"
import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card"
import { DatePicker } from "@/components/ui/DatePicker"
import { Input } from "@/components/ui/Input"
import AddProducts from "./components/AddProducts"
import PlanProducts from "./components/PlanProducts"
import { type PlanProduct, type Product, PLANS, longDate, units } from "./data"

const TODAY = new Date().toISOString().slice(0, 10)

export default function PurchasePlanDetail({ planId }: { planId: string }) {
  const saved = PLANS.find((plan) => plan.id === planId)
  const [name, setName] = useState(saved?.name ?? `Plan, ${longDate(TODAY)}`)
  const [byDate, setByDate] = useState(saved?.byDate ?? "")
  const [planProducts, setPlanProducts] = useState<PlanProduct[]>(saved?.products ?? [])
  const total = planProducts.reduce((sum, p) => sum + p.quantity, 0)

  function add(product: Product) {
    setPlanProducts([
      ...planProducts,
      { productId: product.id, quantity: product.suggested ?? product.moq ?? 0, supplier: product.supplier ?? "", moq: product.moq },
    ])
  }

  function change(productId: string, changes: Partial<PlanProduct>) {
    setPlanProducts(planProducts.map((p) => (p.productId === productId ? { ...p, ...changes } : p)))
  }

  return (
    <AppLayout
      header={
        <PageTitle
          title={name || "Purchase plan"}
          subtitle={`By ${longDate(byDate)} · ${planProducts.length} products · ${units(total)} units`}
          subtitleExtra={<Link href="/wireframes/purchase/plans" className="text-link">All plans</Link>}
        />
      }
    >
      <div className="flex max-w-6xl flex-col gap-4">
        <Card>
          <CardContent className="flex flex-wrap gap-4 pt-6">
            <label className="flex flex-col gap-1 text-sm font-medium">
              Plan name
              <Input className="w-72" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium">
              By date
              <DatePicker className="w-48" value={byDate} onChange={setByDate} />
            </label>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>In this plan</CardTitle>
          </CardHeader>
          <CardContent>
            <PlanProducts
              planProducts={planProducts}
              onChange={change}
              onRemove={(productId) => setPlanProducts(planProducts.filter((p) => p.productId !== productId))}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Add products</CardTitle>
            <CardDescription>Products we think need buying come first.</CardDescription>
          </CardHeader>
          <CardContent>
            <AddProducts addedIds={planProducts.map((p) => p.productId)} onAdd={add} />
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
